import gql from 'graphql-tag';
import { cloneDeep } from 'lodash-es';
import router, { route } from '@router/router';
import i18n from '@assets/i18n';
import { computed, ref } from 'vue';
import baseConfig from '@config/base.config';
import colorConfig from '@config/color.config';
import urlUtils from '@utils/url.utils';
import cookieUtils from '@utils/cookie.utils';
import useEventBus from '@assets/plugins/eventBus';
import { useAccountInfo } from '@models/account/useAccountInfo';
import { useNPPackageInfo } from '@models/package/package';
import { usePackageInfo } from '@models/package/usePackageInfo';
import { useProfileList, Profile } from '@models/profile/profileList';
import { lpModuleUsage, LpModuleUsageType } from '@models/profile/useLpUsage';
import { usePtiUsage, PtiModuleUsageType } from '@models/profile/usePtiUsage';
import { useOtherServiceApi } from '@models/common/useOtherServiceApi';
import {
    roleConfig,
    checkModuleAvailabilityForRole,
    checkRoleIsOwner,
    checkRoleIsReadonly,
    checkRoleIsAnalyzer
} from '@/hooks/useRole';

export type ProductKey = 'insight' | 'experience' | 'landing_page';

export type ProductMenu = {
    key: string;
    usage?: number;
    name: string;
    explain?: string;
    iconColor?: string;
    backgroundColor?: string;
    routerName?: string;
    routerPath?: string;
    pathStartWith?: Array<string>;
    isCurrent?: boolean;
    eventKey: string;
    productKey: ProductKey;
};
interface PtxModuleUsage {
    campaigns: number;
    goals: number;
    userGroups: number;
    monthlyActiveUsers: number;
}

export type Product = {
    key: ProductKey;
    name: 'Insight' | 'Experience' | 'LandingPage';
    logo: string;
    icon: string;
    iconLogo: string;
    icon_short: string;
    activeBg: {
        en: string;
        jp: string;
    };
    color: string;
    theme: 'green';
    default?: boolean;
    describe: string;
    settingTitle: string;
    routerName: string;
    params?: string;
    routePath?: string;
    productIndex: 0 | 1 | 2;
    activeKey: 'pteEnabled' | 'ptxEnabled' | 'lpEnabled';
    roleKey: 'ptiUserRole' | 'ptxUserRole';
    active?: boolean;
    enable?: boolean;
    isCurrent?: boolean;
    packageType?: string;
};

const CHECK_PROFILE_ACTIVE_QUERY = gql`
    query checkActiveProfileFromNewPtengine($where: ProfileWhereUniqueInput!) {
        checkActiveProfileFromNewPtengine(where: $where)
    }
`;

const { eventBus } = useEventBus();
const { account } = useAccountInfo();
const { packageInfo } = usePackageInfo();
const { profileNpPackageInfo } = useNPPackageInfo();
const { profileList } = useProfileList();
const { ptiModuleUsage, ptiModuleUsageLoading } = usePtiUsage();

const profileInfo = ref<Profile>();
const currentProductMenu = ref<ProductMenu | undefined>();
const profileChangeLoading = ref(false);
const domain = urlUtils.getDomain(location.host) || import.meta.env.VITE_COOKIE_DOMAIN;

// 档案是否为新套餐
const profileIsNpPackage = computed(() => profileInfo.value?.isNpPackage);

// 当前账号是否为当前档案创建者
const profileOwnerIsCurrentAccount = computed(
    () => profileInfo.value?.ownerEmail === account.value?.email
);

// 当前档案下产品列表
const profileProductList = computed((): Array<Product> => {
    return cloneDeep(getProductList())?.reduce((acc, cur) => {
        if (cur.key !== 'landing_page' || profileInfo.value?.lpEnabled) {
            acc.push({
                ...cur,
                active: Boolean(profileInfo.value?.[cur.activeKey])
            });
        }
        return acc;
    }, []);
});

// 所有产品列表
const allProductList = computed((): Array<Product> => {
    return cloneDeep(getProductList())?.reduce((acc, cur) => {
        acc.push({
            ...cur,
            active: Boolean(profileInfo.value?.[cur.activeKey])
        });
        return acc;
    }, []);
});

const experienceProduct = computed(() => {
    return allProductList.value?.find(product => product.key === 'experience');
});

const ptiProduct = computed(() => {
    return allProductList.value?.find(product => product.key === 'insight');
});

// Experience使用量
const ptxModuleUsage = computed((): PtxModuleUsage => {
    return profileInfo.value?.isNpPackage
        ? ({
              campaigns: profileNpPackageInfo.value?.ptxPrivilegeUsage?.campaigns,
              goals: profileNpPackageInfo.value?.commonPrivilegeUsage?.ptxConversions,
              userGroups: profileNpPackageInfo.value?.ptxPrivilegeUsage?.userGroups,
              monthlyActiveUsers:
                  profileNpPackageInfo.value?.ptxPrivilegeUsage?.monthlyCampaignViewUsers
          } as PtxModuleUsage)
        : (packageInfo.value?.usage as PtxModuleUsage);
});

// 当前档案所选产品
const profileCurrentProduct = computed(() => {
    const subSidPath = route.value?.path.substr((profileInfo.value?.sid || '').length + 2);
    const lastIndex = subSidPath.indexOf('/');
    const routerProductKey = subSidPath.substr(
        0,
        lastIndex !== -1 ? lastIndex : subSidPath.length
    ) as ProductKey & 'campaign';
    const currentProductKey = routerProductKey === 'campaign' ? 'experience' : routerProductKey;

    return profileProductList.value?.find(product => product.key === currentProductKey);
});

// 当前产品下的导航列表
const profileProductMenuList = computed(() => {
    return cloneDeep(
        getProductMenu({
            isActived: profileInfo.value?.isActived,
            ptiModuleUsage: ptiModuleUsage.value,
            ptxModuleUsage: ptxModuleUsage.value,
            lpModuleUsage: lpModuleUsage.value
        })
    );
});

// 当前选中导航信息
const profileCurrentMenu = computed(() => {
    return (
        profileCurrentProduct.value?.key &&
        profileProductMenuList.value?.[profileCurrentProduct.value?.key]?.find(menu => {
            return (
                (menu?.pathStartWith || []).some((path: string) => {
                    const pathReg = new RegExp(
                        `^\/${profileInfo.value?.sid}/${profileCurrentProduct.value?.key}/${path}`
                    );
                    return pathReg.test(route.value?.path);
                }) || currentProductMenu.value?.key === menu.key
            );
        })
    );
});

export function useProfileInfo() {
    const setProfileInfo = (sid?: string) => {
        const profileId = sid || route.value.params?.sid;
        if (!profileList.value || !profileId) {
            return undefined;
        }

        profileInfo.value = profileList.value.find(profile => profile.sid === profileId);
        profileInfo.value?.sid && updateLocalSid(profileInfo.value?.sid);
    };

    const goToProjectSetting = (sid: string, eventBus?: any) => {
        const settingItem = ProfileSettingMenu.insight.find(item => item.key === 'PROFILE');
        eventBus.$emit('changeProfileMenuItem', settingItem);
        setProfileProductMenu();
        router.push({ path: `/${sid}/insight/setting/profile/base` });
    };

    const setProfileProductMenu = (productMenu?: ProductMenu) => {
        currentProductMenu.value = productMenu;
        productMenu && eventBus.$emit('changeProfileMenuItem', productMenu);
    };

    const checkActiveProfile = async (
        sid: string,
        url: string,
        resolveCallback: () => void,
        rejectCallback: (data: any) => void
    ) => {
        let sidCheckStatus: any;
        const callback = () => {
            sidCheckStatus?.hasCode ? resolveCallback() : rejectCallback(sidCheckStatus);
        };
        if (url) {
            const urlString = url.indexOf('http') > -1 ? url : 'http://' + url;
            const { onOtherServiceApiResult, onOtherServiceApiError } = useOtherServiceApi({
                service: 'draw',
                type: 'post',
                path: '/api/check/trackcode',
                body: {
                    url: urlString,
                    source: 'ptengine',
                    sid
                }
            });
            onOtherServiceApiResult(data => {
                if (data.data.otherServiceApi) {
                    sidCheckStatus = data.data.otherServiceApi?.hasTrackCode;
                    callback();
                }
            });
            onOtherServiceApiError(() => {
                sidCheckStatus = false;
                callback();
            });
        }
    };

    const updateChangeProfileLoading = (status: boolean) => {
        profileChangeLoading.value = status;
    };

    // 校验当前角色下模块的操作权限
    const validateModule = <
        K1 extends keyof typeof roleConfig,
        K2 extends keyof typeof roleConfig[K1],
        K3 extends keyof typeof roleConfig[K1][K2]
    >(
        module: K1,
        subModule: K2,
        profile?: Profile
    ): boolean => {
        const { userRole, ptxEnabled, pteEnabled } = profile || profileInfo.value || {};

        if (!userRole) {
            return false;
        }

        // ptx 优先校验开启状态
        if (module === 'experience' && ptxEnabled === 0) {
            return false;
        }

        // pti 优先校验开启状态
        if (module === 'insight' && pteEnabled === 0) {
            return false;
        }
        return checkModuleAvailabilityForRole(module, subModule, userRole as K3);
    };

    const validateRoleIsOwner = () => {
        const role = profileInfo.value?.userRole;
        return role && checkRoleIsOwner(role);
    };

    const validateRoleIsReadonly = () => {
        const role = profileInfo.value?.userRole;
        return role && checkRoleIsReadonly(role);
    };
    const validateRoleIsAnalyzer = () => {
        const role = profileInfo.value?.userRole;
        return role && checkRoleIsAnalyzer(role);
    };

    const validateProductSetting = () => {
        if (profileCurrentProduct.value?.key === 'experience') {
            return validateModule('experience', 'enableExperienceSetting');
        }

        if (profileCurrentProduct.value?.key === 'landing_page') {
            return validateModule('pageStudio', 'enablePageStudioSetting');
        }

        return validateModule('insight', 'enableInsightSetting');
    };

    /**
     * Insight 内需要校验角色的模块列表
     */
    const getInsightRoleList = (profile?: Profile) => {
        return {
            userRole: profile?.userRole || profileInfo.value?.userRole,
            isOwner: validateRoleIsOwner(),
            adCompatibilitySetting: validateModule('insight', 'adCompatibilitySetting', profile),
            adGenerateURL: validateModule('insight', 'adGenerateURL', profile),
            channelSetting: validateModule('insight', 'channelSetting', profile),
            conversionSetting: validateModule('insight', 'conversionSetting', profile),
            eventSetting: validateModule('insight', 'eventSetting', profile),
            heatmapAdd: validateModule('insight', 'heatmapAdd', profile),
            heatmapReportSetting: validateModule('insight', 'heatmapReportSetting', profile),
            heatmapSetting: validateModule('insight', 'heatmapSetting', profile),
            ipExclusionSetting: validateModule('insight', 'ipExclusionSetting', profile),
            memberSetting: validateModule('member', 'memberSetting', profile),
            pageGroupSetting: validateModule('insight', 'pageGroupSetting', profile),
            planChange: validateModule('plan', 'planChange', profile),
            planUpgrade: validateModule('plan', 'planUpgrade', profile),
            profileAdd: validateModule('insight', 'profileAdd', profile),
            profileDelete: validateModule('insight', 'profileDelete', profile),
            profileSetting: validateModule('insight', 'profileSetting', profile),
            uaExclusionSetting: validateModule('insight', 'uaExclusionSetting', profile),
            urlCombinationSetting: validateModule('insight', 'urlCombinationSetting', profile),
            visitSetting: validateModule('insight', 'visitSetting', profile)
        };
    };

    /**
     * PTX 内需要校验角色的模块列表
     * 与 frontend 项目内 singleSpa.service.ts 一致
     */
    const getExperienceRoleList = () => {
        return {
            userRole: profileInfo.value?.userRole,
            isOwner: validateRoleIsOwner(),
            memberSetting: validateModule('member', 'memberSetting'),
            experienceAdd: validateModule('experience', 'experienceAdd'),
            experienceCopy: validateModule('experience', 'experienceCopy'),
            experienceDelete: validateModule('experience', 'experienceDelete'),
            experienceHeatmap: validateModule('experience', 'experienceHeatmap'),
            experiencePause: validateModule('experience', 'experiencePause'),
            experiencePublish: validateModule('experience', 'experiencePublish'),
            experienceReport: validateModule('experience', 'experienceReport'),
            experienceUpdate: validateModule('experience', 'experienceUpdate'),
            goalAdd: validateModule('experience', 'goalAdd'),
            goalDelete: validateModule('experience', 'goalDelete'),
            goalSaveAs: validateModule('experience', 'goalSaveAs'),
            goalUpdate: validateModule('experience', 'goalUpdate'),
            planUpgrade: validateModule('plan', 'planUpgrade'),
            ptxCrossDomainSetting: validateModule('setting', 'ptxCrossDomainSetting'),
            ptxEventPropertyUpdate: validateModule('setting', 'ptxEventPropertyUpdate'),
            ptxEventUpdate: validateModule('setting', 'ptxEventUpdate'),
            ptxUserPropertyUpdate: validateModule('setting', 'ptxUserPropertyUpdate'),
            userGroupAdd: validateModule('experience', 'userGroupAdd'),
            userGroupCopy: validateModule('experience', 'userGroupCopy'),
            userGroupDelete: validateModule('experience', 'userGroupDelete'),
            userGroupExportUserList: validateModule('experience', 'userGroupExportUserList'),
            userGroupSave: validateModule('experience', 'userGroupSave'),
            userGroupSaveAs: validateModule('experience', 'userGroupSaveAs'),
            userGroupUpdate: validateModule('experience', 'userGroupUpdate'),
            userGroupViewSegment: validateModule('experience', 'userGroupViewSegment')
        };
    };

    return {
        profileInfo,
        profileCurrentProduct,
        profileProductMenuList,
        profileProductList,
        allProductList,
        experienceProduct,
        ptiProduct,
        profileIsNpPackage,
        profileOwnerIsCurrentAccount,
        profileCurrentMenu,
        profileChangeLoading,
        ptiModuleUsageLoading,
        ptxModuleUsage,

        checkActiveProfile,
        getExperienceRoleList,
        getInsightRoleList,
        goToProjectSetting,
        setProfileInfo,
        setProfileProductMenu,
        updateChangeProfileLoading,
        updateLocalSid,
        validateModule,
        validateRoleIsOwner,
        validateRoleIsReadonly,
        validateRoleIsAnalyzer,
        validateProductSetting
    };
}

export function updateLocalSid(sid: string) {
    cookieUtils.setItem({
        sKey: baseConfig.LAST_PROFILE_ID,
        sValue: sid,
        sDomain: domain as string
    });
}

export const ProfileSettingMenu = {
    insight: [
        {
            key: 'PROFILE',
            name: 'header.header_setting.pti_basic_setting', //'Profile Setting',
            icon: 'pt-icon-menu-profile',
            routerName: 'main.setting.profile-base',
            routerPath: '/setting/profile-base',
            pathStartWith: 'setting/profile',
            productKey: 'insight'
        },
        {
            key: 'HEATMAP',
            name: 'header.header_setting.pti_setting_02', //''Heatmap Setting',
            icon: 'pt-icon-header-heatmap',
            routerName: 'main.setting.heatmap-heatmappage',
            routerPath: '/settingheatmap/heatmappage',
            pathStartWith: 'setting/heatmap',
            productKey: 'insight'
        },
        {
            key: 'EVENT',
            name: 'header.header_setting.pti_setting_03', //''Event Setting',
            icon: 'pt-icon-menu-event',
            routerName: 'main.setting.event',
            routerPath: '/setting/event',
            pathStartWith: 'setting/event',
            productKey: 'insight'
        },
        {
            key: 'PAGEGROUP',
            name: 'header.header_setting.pti_setting_04', //'Pagegroup Setting',
            icon: 'pt-icon-menu-pageGroup',
            routerName: 'main.setting.pagegroup',
            routerPath: '/setting/pagegroup',
            pathStartWith: 'setting/pagegroup',
            productKey: 'insight'
        },
        {
            key: 'CONVERSION',
            name: 'header.header_setting.pti_setting_05', //Conversion Setting',
            icon: 'pt-icon-menu-conversion',
            routerName: 'main.setting.conversion',
            routerPath: '/setting/conversion',
            pathStartWith: 'setting/conversion',
            productKey: 'insight'
        },
        {
            key: 'CAMPAIGN',
            name: 'header.header_setting.pti_setting_06', //'Campaign Setting',
            icon: 'pt-icon-menu-campaign',
            routerName: 'main.setting.advertisement-urlbuilder',
            routerPath: '/setting/advertisement/urlbuilder',
            pathStartWith: 'setting/advertisement',
            productKey: 'insight'
        },
        {
            key: 'CHANNEL',
            name: 'header.header_setting.pti_setting_07', //Channel Setting',
            icon: 'pt-icon-menu-sources',
            routerName: 'main.setting.traffictool',
            routerPath: '/setting/traffictool',
            pathStartWith: 'setting/traffictool',
            productKey: 'insight'
        },
        {
            key: 'UNION_MEMBER_MANAGE',
            name: 'header.header_setting.pte_setting_04',
            icon: 'pt-icon-manage',
            routerName: 'Member',
            routerPath: 'member',
            pathStartWith: 'member',
            productKey: 'insight'
        }
    ],
    experience: [
        {
            key: 'dataSetting',
            name: 'header.header_setting.pte_setting_03', //'Data Setting',
            icon: 'pt-icon-data',
            routerName: 'DataSettings',
            routerPath: 'data',
            pathStartWith: 'data',
            productKey: 'experience'
        },
        {
            key: 'UNION_MEMBER_MANAGE',
            name: 'header.header_setting.pte_setting_04',
            icon: 'pt-icon-manage',
            routerName: 'Member',
            routerPath: 'member',
            pathStartWith: 'member',
            productKey: 'experience'
        }
    ],
    landing_page: [
        {
            key: 'domainManage',
            name: 'header.header_setting.lp_domain_setting',
            icon: 'pt-icon-domain',
            routerName: 'LandingPageDomainManagment',
            propductKey: 'landing_page'
        }
    ]
};

export const getProductMenu = ({
    isActived,
    ptiModuleUsage,
    ptxModuleUsage,
    lpModuleUsage
}: {
    isActived?: number | undefined;
    ptiModuleUsage?: PtiModuleUsageType;
    ptxModuleUsage?: PtxModuleUsage;
    lpModuleUsage?: LpModuleUsageType;
}): Record<ProductKey, Array<ProductMenu>> => ({
    insight: [
        {
            key: 'DATACENTER',
            name: 'header.sub_menu.pti_dc_title', //'Data center',
            explain: 'header.sub_menu.pti_dc_title_explain',
            routerName: isActived === 1 ? 'main.datacenter.overview' : 'main.datacenter.onboarding',
            pathStartWith: ['datacenter'],
            routerPath: isActived === 1 ? 'overview' : 'onboarding',
            eventKey: 'pti_dc',
            productKey: 'insight',
            iconColor: 'rgba(47, 205, 100, 0.12)',
            backgroundColor: 'rgba(47, 205, 100, 0.08)'
        },
        {
            key: 'EVENT',
            name: 'header.sub_menu.pti_event_title', //'Event',
            explain: 'header.sub_menu.pti_event_title_explain',
            usage: ptiModuleUsage?.event,
            routerName:
                !ptiModuleUsage?.event || !isActived
                    ? 'main.event.onboarding'
                    : 'main.event.segment',
            pathStartWith: ['event'],
            routerPath: !ptiModuleUsage?.event || !isActived ? 'onboarding' : 'segment',
            eventKey: 'pti_event',
            productKey: 'insight',
            iconColor: 'rgba(80, 190, 250, 0.12)',
            backgroundColor: 'rgba(80, 190, 250, 0.08)'
        },
        {
            key: 'CONVERSION',
            name: 'header.sub_menu.pti_cv_title', //'Conversion',
            explain: 'header.sub_menu.pti_cv_title_explain',
            usage: ptiModuleUsage?.conversion,
            routerName:
                !ptiModuleUsage?.conversion || (!ptiModuleUsage?.conversion && !isActived)
                    ? 'main.conversion.onboarding'
                    : 'main.conversion.overview',
            pathStartWith: ['conversion'],
            routerPath:
                !ptiModuleUsage?.conversion || (!ptiModuleUsage?.conversion && !isActived)
                    ? 'onboarding'
                    : 'overview',
            eventKey: 'pti_cv',
            productKey: 'insight',
            iconColor: 'rgba(146, 102, 214, 0.12)',
            backgroundColor: 'rgba(146, 102, 214, 0.08)'
        },
        {
            // key: 'HEATMAP',
            key: 'PAGESCENE',
            name: 'header.sub_menu.pti_heatmap_title',
            explain: 'header.sub_menu.pti_heatmap_title_explain',
            usage: ptiModuleUsage?.heatmap,
            routerName:
                !ptiModuleUsage?.heatmap || (ptiModuleUsage?.heatmap <= 0 && !isActived)
                    ? 'main.pagescene.onboarding'
                    : 'main.pagescene.pagescenelist',
            pathStartWith: ['pagescene'],
            routerPath:
                !ptiModuleUsage?.heatmap || (ptiModuleUsage?.heatmap <= 0 && !isActived)
                    ? 'onboarding'
                    : 'pagescenelist',
            eventKey: 'pti_hm',
            productKey: 'insight',
            iconColor: 'rgba(254, 141, 85, 0.12)',
            backgroundColor: 'rgba(254, 141, 85, 0.08)'
        }
    ],
    experience: [
        {
            key: 'CAMPAIGN',
            usage: ptxModuleUsage?.campaigns,
            name: 'header.sub_menu.exp_personalization',
            explain: 'header.sub_menu.exp_personalization_explain',
            routerName: 'CampaignReport',
            pathStartWith: ['campaign'],
            eventKey: 'ptx_cp',
            productKey: 'experience',
            iconColor: 'rgba(80, 190, 250, 0.12)',
            backgroundColor: 'rgba(80, 190, 250, 0.08)'
        },
        {
            key: 'USERS',
            usage: ptxModuleUsage?.userGroups,
            name: 'header.sub_menu.exp_users',
            explain: 'header.sub_menu.exp_users_explain',
            routerName: 'Users', //与【ptx/src/router/app.router.js】 一致
            pathStartWith: ['users'],
            eventKey: 'ptx_ug',
            productKey: 'experience',
            iconColor: 'rgba(47, 205, 100, 0.12)',
            backgroundColor: 'rgba(47, 205, 100, 0.08)'
        },
        {
            key: 'GOALS',
            usage: ptxModuleUsage?.goals,
            name: 'header.sub_menu.exp_goals',
            explain: 'header.sub_menu.exp_goals_explain',
            routerName: 'Goals',
            pathStartWith: ['goals'],
            eventKey: 'ptx_goal',
            productKey: 'experience',
            iconColor: 'rgba(254, 141, 85, 0.12)',
            backgroundColor: 'rgba(254, 141, 85, 0.08)'
        }
    ],
    landing_page: [
        {
            key: 'landing_page',
            usage: lpModuleUsage?.Created,
            name: 'header.sub_menu.landing_page',
            routerName: 'LandingPageList', //LandingPageList  LandingPageOnboarding
            pathStartWith: ['list'],
            eventKey: 'lp_page',
            productKey: 'landing_page'
        }
    ]
});

export const getProductList = (): Array<Product> => [
    {
        key: 'insight',
        name: 'Insight',
        logo: `${import.meta.env.VITE_BASE_URL}icons/pt-logo-i-gw.svg`,
        icon: `${import.meta.env.VITE_BASE_URL}icons/pt-logo-i-gb.svg`,
        activeBg: {
            en: `${import.meta.env.VITE_BASE_URL}images/bg-insight-active.jpg`,
            jp: `${import.meta.env.VITE_BASE_URL}images/bg-insight-active.jpg`
        },
        iconLogo: 'pt-logo-i-gb',
        icon_short: 'PTI-icon',
        color: colorConfig['$pt-green-60'],
        theme: 'green',
        default: true,
        settingTitle: i18n.global.t('header.header_setting.pti_profile_setting'), //'Insight Project Settings',
        routerName: 'Insight',
        routePath: 'insight',
        activeKey: 'pteEnabled',
        roleKey: 'ptiUserRole',
        productIndex: 0,
        describe: 'profile_list.insight_introduce'
    },
    {
        key: 'experience',
        name: 'Experience',
        logo: `${import.meta.env.VITE_BASE_URL}icons/pt-logo-x-gw.svg`,
        icon: `${import.meta.env.VITE_BASE_URL}icons/pt-logo-x-gb.svg`,
        activeBg: {
            en: `${import.meta.env.VITE_BASE_URL}images/bg-experience-active-en.jpg`,
            jp: `${import.meta.env.VITE_BASE_URL}images/bg-experience-active-jp.jpg`
        },
        iconLogo: 'pt-logo-x-gb',
        icon_short: 'PTX-icon',
        color: colorConfig['$pt-green-60'],
        theme: 'green',
        settingTitle: i18n.global.t('header.header_setting.pte_profile_setting'), //'Experience Project Settings',
        routerName: 'ExperienceRoot',
        routePath: 'experience',
        activeKey: 'ptxEnabled',
        roleKey: 'ptxUserRole',
        productIndex: 1,
        describe: 'profile_list.experience_introduce'
    },
    {
        key: 'landing_page',
        name: 'LandingPage',
        logo: `${import.meta.env.VITE_BASE_URL}icons/pt-logo-ps-gw.svg`,
        icon: `${import.meta.env.VITE_BASE_URL}icons/pt-logo-x-gb.svg`,
        activeBg: {
            en: `${import.meta.env.VITE_BASE_URL}images/bg-landingpage-active-en.png`,
            jp: `${import.meta.env.VITE_BASE_URL}images/bg-landingpage-active-jp.png`
        },
        iconLogo: 'pt-logo-ps-gb',
        icon_short: 'LP-icon',
        color: colorConfig['$pt-green-60'],
        theme: 'green',
        settingTitle: i18n.global.t('header.header_setting.lp_profile_setting'), //'Experience Project Settings',
        routerName: 'LandingPage',
        routePath: 'landing_page',
        activeKey: 'lpEnabled', //'landingPageEnabled， lpEnabled',
        roleKey: 'ptiUserRole', // 'landingPageUserRole',
        productIndex: 2,
        describe: 'profile_list.landingpage_introduce'
    }
];
